/**
 * Tracks per-recording listening progress in localStorage so users can
 * "pick up where they left off" across visits. Keeps the last 30 items.
 */

const KEY = 'vaad_resume_v1';
const MAX_ITEMS = 30;
const MIN_SAVE_INTERVAL_MS = 4000;
const MIN_TRACKED_SECONDS = 8; // don't save if listened for less than 8s

const readAll = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeAll = (obj) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(obj));
  } catch { /* quota */ }
};

const trim = (obj) => {
  const entries = Object.entries(obj);
  if (entries.length <= MAX_ITEMS) return obj;
  entries.sort((a, b) => (b[1].updated_at || 0) - (a[1].updated_at || 0));
  return Object.fromEntries(entries.slice(0, MAX_ITEMS));
};

export const getResumePosition = (recordingId) => {
  if (!recordingId) return null;
  const all = readAll();
  const entry = all[recordingId];
  if (!entry || !entry.position || entry.completed) return null;
  return entry;
};

/**
 * Return one of:
 *   { status: 'unheard' }
 *   { status: 'partial', percent: 42 }
 *   { status: 'done' }
 * so RecordingCard can render a listened badge WITHOUT needing to load
 * the full resume payload for every tile. Cap resume.percent at 97 so
 * we never render "97%" on something that's already marked completed.
 */
export const getListenStatus = (recordingId) => {
  if (!recordingId) return { status: 'unheard' };
  const entry = readAll()[recordingId];
  if (!entry) return { status: 'unheard' };
  if (entry.completed) return { status: 'done' };
  if ((entry.percent || 0) > 3) return { status: 'partial', percent: entry.percent };
  return { status: 'unheard' };
};


export const saveResumePosition = (recording, currentTime, duration) => {
  if (!recording || !recording.drive_file_id || !currentTime || !duration) return;
  if (currentTime < MIN_TRACKED_SECONDS) return;
  const all = readAll();
  const existing = all[recording.drive_file_id];
  const now = Date.now();
  if (existing && (now - (existing.updated_at || 0)) < MIN_SAVE_INTERVAL_MS && !existing.completed) return;

  const completed = duration > 0 && currentTime / duration > 0.97;
  all[recording.drive_file_id] = {
    id: recording.drive_file_id,
    title: recording.title,
    speaker: recording.speaker,
    cover_image: recording.cover_image,
    position: completed ? 0 : Math.floor(currentTime),
    duration: Math.floor(duration),
    percent: duration > 0 ? Math.min(100, Math.round((currentTime / duration) * 100)) : 0,
    updated_at: now,
    completed,
  };
  writeAll(trim(all));
};

export const clearResume = (recordingId) => {
  if (!recordingId) return;
  const all = readAll();
  delete all[recordingId];
  writeAll(all);
};

export const listResume = (limit = 10) => {
  const all = readAll();
  return Object.values(all)
    .filter((e) => e.position && !e.completed)
    .sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0))
    .slice(0, limit);
};
